import { CopyOutlined } from '@ant-design/icons';
import { dbCols, Message } from '@cc/schema';
import { List, useTable } from '@refinedev/antd';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { Button, Modal, Space, Table, Typography } from 'antd';
import { useCustomMutation, useGetLocale, useTranslate } from '@refinedev/core';
import { format } from 'date-fns';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ClientName,
  ShowNamesButton,
  useClientNamesVisibility,
} from '../../components/clientNames';

import { MutationResolvers } from '../../schema/be-graphql-generated';

const { Text, Title } = Typography;

export const MessageList = () => {
  const translate = useTranslate();
  // F-b19 (founder 2026-09-21): reading a client's messages is review time on the filtered client
  useActivityTracking(dbCols.messages);

  const locale = useGetLocale();

  const currentLocale = locale();

  const [modalText, setModalText] = useState('');

  // Q-mt-3b A: the sender as "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();

  const { mutate } =
    // @ts-ignore
    useCustomMutation<MutationResolvers['updateMessageRead']>();

  const [searchParams] = useSearchParams();
  const fromClient = searchParams.get('client') || undefined;

  const { tableProps, setFilters, filters, tableQueryResult } =
    useTable<Message>({
      queryOptions: {
        select: (data) => {
          // @ts-ignore
          return { data: data.data.data, total: data.total };
        },
      },

      meta: {
        operation: 'therapistMessages',
        fields: [
          {
            data: [
              '_id',
              'createdAt',
              'readAt',
              'from',
              'to',
              'message',
              {
                fromUser: ['email', 'firstName', 'lastName'],
              },
            ],
          },
          'count',
        ],
      },

      sorters: {
        initial: [
          {
            field: 'createdAt',
            order: 'asc',
          },
        ],
      },
      // E57 · the client page's Message button — this client's messages (`?client=users/<key>`)
      filters: {
        permanent: fromClient
          ? [{ field: 'from', operator: 'eq', value: fromClient }]
          : [],
      },
    });

  const updateMessage = async (id: string) => {
    await mutate({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation: 'updateMessageRead',
        fields: ['success'],
        variables: {
          input: {
            name: 'input',
            type: 'MessageReadInput!',
            value: {
              where: { id_eq: id },
            },
          },
        },
      },
    });

    await tableQueryResult.refetch();
  };

  return (
    <List
      title={translate('messages.messages')}
      headerProps={{
        extra: (
          <ShowNamesButton
            visible={names.visible}
            onToggle={names.toggle}
            testId="cc-messages-toggle-names"
          />
        ),
      }}
    >
      <Modal
        destroyOnClose
        cancelButtonProps={{ style: { display: 'none' } }}
        open={!!modalText}
        onCancel={() => setModalText('')}
        onOk={() => setModalText('')}
      >
        <div>{modalText}</div>
      </Modal>
      <Table {...tableProps} rowKey="_id" data-testid="cc-messages-list">
        <Table.Column
          key="createdAt"
          dataIndex="createdAt"
          title={translate('messages.fields.createdAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss aaa',
            );
          }}
        />
        <Table.Column
          key="readAt"
          dataIndex="readAt"
          title={translate('messages.fields.readAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss aaa',
            );
          }}
        />
        <Table.Column<Message>
          key="email"
          title={translate('messages.fields.from')}
          render={(text, record, indx) => {
            // E50: a sender who was erased — the practice keeps the message, the person is gone
            if (!record.fromUser)
              return (
                <Text type="secondary" data-testid="cc-message-deleted-sender">
                  {translate('messages.deletedSender')}
                </Text>
              );
            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>
                  <ClientName
                    id={record.from}
                    firstName={record.fromUser?.firstName}
                    lastName={record.fromUser?.lastName}
                    visible={names.visible}
                  />
                </Text>
                <Link
                  to="#"
                  onClick={(e) => {
                    window.open(
                      `mailto:${record.fromUser?.email || ''}`,
                      '_blank',
                    );
                    e.preventDefault();
                  }}
                >
                  {record.fromUser?.email}
                </Link>
              </div>
            );
          }}
        />
        {/* <Table.Column
          key="to"
          dataIndex="to"
          title={translate('messages.fields.to')}
        /> */}
        <Table.Column
          title={translate('messages.fields.subject')}
          render={(_, record) => (
            <Text>{translate('messages.defaultSubject')}</Text>
          )}
        />
        <Table.Column<Message>
          key="message"
          dataIndex="message"
          title={translate('messages.fields.message')}
          render={(text, record, indx) => {
            return (
              <Link
                to=""
                onClick={() => {
                  setModalText(record.message);
                  if (!record.readAt) updateMessage(record._id);
                }}
              >
                {translate('messages.readMessage')}
              </Link>
            );
          }}
        />
        <Table.Column<Message>
          title={translate('resourcesCommon.actions')}
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              <Button
                size="small"
                icon={<CopyOutlined />}
                title={translate('messages.fields.copyMessage')}
                onClick={() => {
                  navigator.clipboard.writeText(record.message);
                }}
              >
                {translate('messages.fields.copyMessage')}
              </Button>
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
